import { get, isEmpty } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { Loader, ProgressBar, Text } from 'src/@vymo/ui/atoms';
import { Card, Tooltip } from 'src/@vymo/ui/blocks';
import { ReactComponent as PerformanceIcon } from 'src/assets/icons/performance.svg';
import { Keys, locale } from 'src/i18n';
import { getCardViewData, processCardData } from './queries';
import { CardData, PerformanceSnapshotCardProps } from './types';
import styles from './index.module.scss';

function PerformanceSnapshotCard({
  snapshotCard,
  adjustNumberOfCards,
  metricsData,
}: PerformanceSnapshotCardProps) {
  const [cardData, setCardData] = useState<CardData>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const apiFailure = useCallback(
    (errorMsg = locale(Keys.FAILED_TO_LOAD_PERFORMANCE_METRICS_DATA)) => {
      setError(errorMsg);
    },
    [],
  );

  const canShowSnapshotCard = useCallback(
    () =>
      (cardData && cardData.show_snapshot_card === true) ||
      !isEmpty(error) ||
      loading,
    [cardData, error, loading],
  );

  useEffect(() => {
    const processedData = processCardData(
      snapshotCard,
      metricsData,
      apiFailure,
    );
    setCardData(processedData.cardData);
    setLoading(processedData.loading);
    if (processedData.error) {
      setError(processedData.error);
    } else {
      setError('');
    }
  }, [snapshotCard, metricsData, apiFailure]);

  useEffect(() => {
    if (!canShowSnapshotCard()) {
      adjustNumberOfCards(`${cardData?.type}-${cardData?.title}`);
    }
  }, [adjustNumberOfCards, cardData, canShowSnapshotCard]);

  const routeToPerformanceTab = useCallback(() => {
    if (!isEmpty(error)) {
      // Retry logic could go here
    } else {
      // TODO: Replace with your web navigation
      // Example: navigate('/performance', { state: { snapshotCard: cardData } });
    }
  }, [error]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        routeToPerformanceTab();
      }
    },
    [routeToPerformanceTab],
  );

  // eslint-disable-next-line complexity
  const renderMetricCard = useCallback((card: CardData) => {
    // getCardViewData expects: context.metricsData.result.metric
    // card.result is the body from API: { result: { metric: {...} }, progress_type_settings: [...] }
    const viewData = getCardViewData({
      context: {
        metricsData: {
          result: card.result?.result || card.result,
          progress_type_settings: card.result?.progress_type_settings,
        },
      },
    });

    const metric = card.result?.result?.metric || card.result?.metric;
    const progressValue = metric?.value || 0;
    const progressMax = metric?.target?.value || viewData.pbMax || 100;
    const progressPercentage = metric?.target?.progress_percentage || 0;

    return (
      <div className={styles.performanceSnapshotCard__metric}>
        <Text type="h5">{viewData.actualExpectedCount}</Text>
        {viewData.showProgressBar && progressMax > 0 && (
          <div className={styles.performanceSnapshotCard__progressContainer}>
            <div className={styles.performanceSnapshotCard__progressBar}>
              <ProgressBar
                value={progressValue}
                maxValue={progressMax}
                strokeColor={viewData.pbFillColor}
                size="small"
                showText={false}
                trailColor="var(--background-alpha-default)"
              />
            </div>
            <Text classNames={styles.performanceSnapshotCard__progressText}>
              {progressPercentage}%
            </Text>
          </div>
        )}
      </div>
    );
  }, []);

  const renderByCardType = (card: CardData | undefined) => {
    if (!card) return null;

    switch (card.type) {
      case 'metric':
        return renderMetricCard(card);
      case 'leaderboard-myranking': {
        const points = get(card, 'result.self.rows.0.points', '');
        const description = get(card, 'result.self.description', '');
        return (
          <div>
            <Text
              classNames={styles.performanceSnapshotCard__value}
            >{`#${points}`}</Text>
            {!isEmpty(description) && (
              <div
                className={styles.performanceSnapshotCard__chips}
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: description }}
              />
            )}
          </div>
        );
      }
      default:
        return null;
    }
  };

  const getCardView = () => {
    if (!isEmpty(error)) {
      return (
        <Card classNames={styles['performanceSnapshotCard--error']}>
          <div className={styles.performanceSnapshotCard__errorMessage}>
            {error}
          </div>
        </Card>
      );
    }

    return (
      <Card classNames={styles.performanceSnapshotCard}>
        <div className={styles.performanceSnapshotCard__header}>
          <div className={styles.performanceSnapshotCard__headerIcon}>
            <PerformanceIcon />
          </div>
          {cardData?.title && (
            <Tooltip
              tooltipClass={styles.performanceSnapshotCard__titleTooltip}
              content={cardData.title}
              placement="top"
              showIcon={false}
            >
              <div className={styles.performanceSnapshotCard__titleWrapper}>
                <Text classNames={styles.performanceSnapshotCard__title}>
                  {cardData.title}
                </Text>
              </div>
            </Tooltip>
          )}
        </div>
        <div className={styles.performanceSnapshotCard__dataContainer}>
          {renderByCardType(cardData)}
        </div>
      </Card>
    );
  };

  if (!canShowSnapshotCard()) {
    return null;
  }

  return (
    <div
      className={styles.performanceSnapshotCard}
      onClick={routeToPerformanceTab}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
    >
      {loading ? <Loader visible={loading} size="small" /> : getCardView()}
    </div>
  );
}

export default PerformanceSnapshotCard;
